import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ChatMessageCreatedEvent,
  ChatRealtimeService,
} from './chat-realtime.service';
import { ChatMessageDto, ChatSummaryDto } from './chat.dto';
import { ChatService } from './chat.service';
import { AceAuthService } from '../services/auth/ace-auth.service';

export interface ChatMessagePopup {
  chatId: string;
  messageId: string;
  title: string;
  body: string;
  initials: string;
}

interface ChatUnreadSyncMessage {
  type: 'refresh';
  id: string;
  senderId: string;
  userId: string;
}

const CHAT_UNREAD_CHANNEL = 'ace-chat-unread-v1';
const CHAT_UNREAD_STORAGE_KEY = 'ace-chat-unread-sync-v1';

@Injectable({ providedIn: 'root' })
export class ChatNotificationService implements OnDestroy {
  private readonly popupSubject = new BehaviorSubject<ChatMessagePopup | null>(
    null
  );
  readonly popup$ = this.popupSubject.asObservable();
  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  readonly unreadCount$ = this.unreadCountSubject.asObservable();
  private readonly openChatSubject = new Subject<string>();
  readonly openChat$ = this.openChatSubject.asObservable();
  private readonly createChatSubject = new BehaviorSubject(false);
  readonly createChat$ = this.createChatSubject.asObservable();
  private readonly sidebarCollapsedSubject = new BehaviorSubject(false);
  readonly sidebarCollapsed$ = this.sidebarCollapsedSubject.asObservable();
  private readonly subscriptions = new Subscription();
  private readonly chats = new Map<string, ChatSummaryDto>();
  private readonly globallyJoinedChatIds = new Set<string>();
  private readonly seenMessageIds = new Set<string>();
  private readonly seenUnreadSyncIds = new Set<string>();
  private readonly tabId = createTabId();
  private unreadRefreshRequest = 0;
  private unreadRefreshSubscription: Subscription | null = null;
  private unreadRefreshTimer: number | null = null;
  private popupDismissTimer: number | null = null;
  private chatIndexRequest = 0;
  private unreadChannel: BroadcastChannel | null = null;
  private storageListener: ((event: StorageEvent) => void) | null = null;
  private visibilityListener: (() => void) | null = null;
  private started = false;
  private pendingChatId: string | null = null;
  private messagePopupAudioContext: AudioContext | null = null;

  constructor(
    private readonly chatRealtime: ChatRealtimeService,
    private readonly chatService: ChatService,
    private readonly aceAuthService: AceAuthService,
    private readonly router: Router
  ) {}

  start(): void {
    if (this.started || !environment.teamApiUrl?.trim()) return;
    this.started = true;
    this.bindUnreadSync();
    this.subscriptions.add(
      this.chatRealtime.reconnected$.subscribe(() => {
        this.refreshChatIndex();
        this.refreshUnreadCount(false);
      })
    );
    this.subscriptions.add(
      this.chatRealtime.connect().subscribe({ error: () => undefined })
    );
    this.subscriptions.add(
      this.chatRealtime.messageCreated$.subscribe(event =>
        this.handleIncoming(event)
      )
    );
    this.subscriptions.add(
      this.chatRealtime.chatAudit$.subscribe(event => {
        if (event.message) {
          this.handleIncoming({ chatId: event.chatId, message: event.message });
        } else {
          this.refreshUnreadCount(false);
        }
      })
    );
    this.refreshChatIndex();
    this.refreshUnreadCount(false);
  }

  refreshUnreadCount(broadcast = true): void {
    if (!this.started || !environment.teamApiUrl?.trim()) return;
    if (broadcast) this.broadcastUnreadRefresh();
    if (this.unreadRefreshTimer !== null) {
      window.clearTimeout(this.unreadRefreshTimer);
    }
    this.unreadRefreshTimer = window.setTimeout(() => {
      this.unreadRefreshTimer = null;
      const request = ++this.unreadRefreshRequest;
      this.unreadRefreshSubscription?.unsubscribe();
      this.unreadRefreshSubscription = this.chatService
        .getUnreadCount()
        .subscribe({
          next: response => {
            if (request !== this.unreadRefreshRequest) return;
            const count = Number(response?.unreadCount);
            this.unreadCountSubject.next(
              Number.isFinite(count) ? Math.max(0, count) : 0
            );
          },
          error: () => undefined,
        });
      this.subscriptions.add(this.unreadRefreshSubscription);
    }, 75);
  }

  openPopup(): void {
    const popup = this.popupSubject.value;
    const chatId = popup?.chatId || null;
    this.dismissPopup();
    if (!chatId) return;
    this.openChatById(chatId);
  }

  openChatById(chatId: string): void {
    if (!chatId) return;
    const currentPath = this.router.url.split(/[?#]/)[0].replace(/\/+$/, '');
    if (currentPath.endsWith('/chat')) {
      this.openChatSubject.next(chatId);
      return;
    }
    this.pendingChatId = chatId;
    void this.router.navigate(this.chatRoute());
  }

  requestCreateChat(): void {
    this.createChatSubject.next(true);
  }

  clearCreateChatRequest(): void {
    this.createChatSubject.next(false);
  }

  get sidebarCollapsed(): boolean {
    return this.sidebarCollapsedSubject.value;
  }

  toggleSidebar(): void {
    this.sidebarCollapsedSubject.next(!this.sidebarCollapsedSubject.value);
  }

  dismissPopup(): void {
    if (this.popupDismissTimer !== null) {
      window.clearTimeout(this.popupDismissTimer);
      this.popupDismissTimer = null;
    }
    this.popupSubject.next(null);
  }

  consumePendingChatId(): string | null {
    const chatId = this.pendingChatId;
    this.pendingChatId = null;
    return chatId;
  }

  ngOnDestroy(): void {
    this.started = false;
    this.dismissPopup();
    this.subscriptions.unsubscribe();
    this.unreadRefreshRequest++;
    if (this.unreadRefreshTimer !== null) {
      window.clearTimeout(this.unreadRefreshTimer);
      this.unreadRefreshTimer = null;
    }
    this.unreadRefreshSubscription = null;
    this.globallyJoinedChatIds.clear();
    this.chats.clear();
    this.seenUnreadSyncIds.clear();
    this.unreadChannel?.close();
    this.unreadChannel = null;
    if (this.storageListener) {
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', this.storageListener);
      }
      this.storageListener = null;
    }
    if (this.visibilityListener) {
      if (typeof document !== 'undefined') {
        document.removeEventListener(
          'visibilitychange',
          this.visibilityListener
        );
      }
      this.visibilityListener = null;
    }
    void this.messagePopupAudioContext?.close().catch(() => undefined);
    this.messagePopupAudioContext = null;
  }

  private refreshChatIndex(): void {
    const request = ++this.chatIndexRequest;
    this.subscriptions.add(
      this.chatService.listChats({ archived: false }).subscribe({
        next: response => {
          if (request !== this.chatIndexRequest) return;
          this.syncActiveChats(response.items ?? []);
        },
        error: () => undefined,
      })
    );
  }

  /**
   * Register chats discovered by either the global index or the chat page.
   * This keeps notifications working after navigating away from the chat
   * route, including for a chat created during the current session.
   */
  trackChats(chats: readonly ChatSummaryDto[]): void {
    chats.forEach(chat => {
      if (!chat?.id) return;
      this.chats.set(chat.id, chat);
      if (!this.started || this.globallyJoinedChatIds.has(chat.id)) return;

      this.globallyJoinedChatIds.add(chat.id);
      this.subscriptions.add(
        this.chatRealtime.join(chat.id).subscribe({
          error: () => this.globallyJoinedChatIds.delete(chat.id),
        })
      );
    });
  }

  /**
   * Reconcile the global room set with the active chat index. The chat page
   * calls this after a list refresh so archived/deleted chats do not keep a
   * stale realtime subscription forever.
   */
  syncActiveChats(chats: readonly ChatSummaryDto[]): void {
    const activeChatIds = new Set(chats.map(chat => chat.id).filter(Boolean));
    [...this.globallyJoinedChatIds]
      .filter(chatId => !activeChatIds.has(chatId))
      .forEach(chatId => {
        this.globallyJoinedChatIds.delete(chatId);
        this.chats.delete(chatId);
        this.subscriptions.add(
          this.chatRealtime.leave(chatId).subscribe({ error: () => undefined })
        );
      });
    this.trackChats(chats);
  }

  private handleIncoming(event: ChatMessageCreatedEvent): void {
    const message = event.message;
    if (
      !event.chatId ||
      !message ||
      message.chatId !== event.chatId ||
      !message.id
    ) {
      return;
    }
    if (this.seenMessageIds.has(message.id)) return;
    this.seenMessageIds.add(message.id);
    if (this.seenMessageIds.size > 200) {
      const oldest = this.seenMessageIds.values().next().value;
      if (oldest) this.seenMessageIds.delete(oldest);
    }

    const chat = this.chats.get(event.chatId);
    if (this.isOwnMessage(event.chatId, message)) return;

    this.showPopup({
      chatId: event.chatId,
      messageId: message.id,
      title: chat?.title || this.messageAuthor(message),
      body: this.messagePreview(message),
      initials: chat ? this.chatInitials(chat) : this.messageInitials(message),
    });
    this.refreshUnreadCount(false);

    if (!chat) {
      this.subscriptions.add(
        this.chatService.getChat(event.chatId).subscribe({
          next: summary => this.trackChats([summary]),
          error: () => undefined,
        })
      );
    }
  }

  private bindUnreadSync(): void {
    if (this.unreadChannel || this.storageListener) return;

    if (typeof BroadcastChannel !== 'undefined') {
      try {
        const channel = new BroadcastChannel(CHAT_UNREAD_CHANNEL);
        channel.onmessage = event => this.handleUnreadSyncMessage(event.data);
        this.unreadChannel = channel;
      } catch {
        // Private browsing can expose BroadcastChannel but reject construction.
      }
    }

    if (typeof window === 'undefined') return;
    this.storageListener = event => {
      if (event.key !== CHAT_UNREAD_STORAGE_KEY || !event.newValue) return;
      try {
        this.handleUnreadSyncMessage(JSON.parse(event.newValue));
      } catch {
        // Ignore malformed storage events from another tab or an extension.
      }
    };
    window.addEventListener('storage', this.storageListener);
    if (typeof document !== 'undefined') {
      this.visibilityListener = () => {
        if (document.visibilityState === 'visible') {
          this.refreshUnreadCount(false);
        }
      };
      document.addEventListener('visibilitychange', this.visibilityListener);
    }
  }

  private broadcastUnreadRefresh(): void {
    const userId = this.aceAuthService.getUserId();
    if (!userId) return;
    const message: ChatUnreadSyncMessage = {
      type: 'refresh',
      id: createTabId(),
      senderId: this.tabId,
      userId,
    };
    try {
      this.unreadChannel?.postMessage(message);
    } catch {
      // A tab may close its channel while a scheduled refresh is in flight.
    }
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(
          CHAT_UNREAD_STORAGE_KEY,
          JSON.stringify(message)
        );
      } catch {
        // Storage can be unavailable in private browsing or restrictive embeds.
      }
    }
  }

  private handleUnreadSyncMessage(value: unknown): void {
    if (!isChatUnreadSyncMessage(value)) return;
    if (value.senderId === this.tabId) return;
    if (value.userId !== this.aceAuthService.getUserId()) return;
    if (this.seenUnreadSyncIds.has(value.id)) return;
    this.seenUnreadSyncIds.add(value.id);
    if (this.seenUnreadSyncIds.size > 100) {
      const oldest = this.seenUnreadSyncIds.values().next().value;
      if (oldest) this.seenUnreadSyncIds.delete(oldest);
    }
    this.refreshUnreadCount(false);
  }

  private isOwnMessage(chatId: string, message: ChatMessageDto): boolean {
    if (
      message.authorId &&
      message.authorId === this.aceAuthService.getUserId()
    ) {
      return true;
    }
    return message.authorId === this.chats.get(chatId)?.myMember?.id;
  }

  private showPopup(popup: ChatMessagePopup): void {
    this.dismissPopup();
    this.popupSubject.next(popup);
    this.popupDismissTimer = window.setTimeout(() => {
      this.popupDismissTimer = null;
      this.dismissPopup();
    }, 5000);
    this.playMessagePopupSound();
  }

  private playMessagePopupSound(): void {
    try {
      const AudioContextConstructor =
        window.AudioContext ??
        (window as Window & { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!AudioContextConstructor) return;

      const context =
        this.messagePopupAudioContext ??
        (this.messagePopupAudioContext = new AudioContextConstructor());
      const now = context.currentTime;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(680, now);
      oscillator.frequency.linearRampToValueAtTime(860, now + 0.08);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.035, now + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.17);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(now);
      oscillator.stop(now + 0.18);
      if (context.state === 'suspended') {
        void context.resume().catch(() => undefined);
      }
    } catch {
      // Browsers can block audio until the page has received a user gesture.
    }
  }

  private chatRoute(): string[] {
    const segments = this.router.url
      .split(/[?#]/)[0]
      .split('/')
      .filter(Boolean);
    const gpIndex = segments.indexOf('gp');
    const vehicleIndex = segments.indexOf('vehicle');
    const gpId = gpIndex >= 0 ? segments[gpIndex + 1] : '';
    const vehicleId = vehicleIndex >= 0 ? segments[vehicleIndex + 1] : '';
    return gpId && vehicleId
      ? ['/gp', gpId, 'vehicle', vehicleId, 'chat']
      : ['/'];
  }

  private messageAuthor(message: ChatMessageDto): string {
    if (message.authorId === 'spike-team') return 'Spike Team';
    const author = message.author;
    if (!author) return 'New message';
    return (
      [author.firstName ?? author.name, author.lastName ?? author.surname]
        .filter(Boolean)
        .join(' ') ||
      author.email ||
      'New message'
    );
  }

  private messagePreview(message: ChatMessageDto): string {
    return (
      message.body?.trim() ||
      (message.files?.length ? 'Attachment' : 'New message')
    );
  }

  private chatInitials(chat: ChatSummaryDto): string {
    return this.initials(chat.title);
  }

  private messageInitials(message: ChatMessageDto): string {
    return this.initials(this.messageAuthor(message));
  }

  private initials(value: string): string {
    const letters = value
      .split(/\s+/)
      .map(part => part[0])
      .filter(Boolean)
      .join('')
      .slice(0, 2);
    return letters.toUpperCase() || '?';
  }
}

function createTabId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

function isChatUnreadSyncMessage(
  value: unknown
): value is ChatUnreadSyncMessage {
  if (!value || typeof value !== 'object') return false;
  const message = value as Partial<ChatUnreadSyncMessage>;
  return (
    message.type === 'refresh' &&
    typeof message.id === 'string' &&
    typeof message.senderId === 'string' &&
    typeof message.userId === 'string'
  );
}
